import React, { useState } from 'react';
import { Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from "../../stores/authStore";
import api from '../../services/api';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';

interface LoginForm {
  username: string;
  password: string;
}

const GOOGLE_CLIENT_ID = '681023234697-beqpuu6ivs8ggavreuila58v3s6r83lv.apps.googleusercontent.com'; // Thay bằng client id thực tế

const LoginPage: React.FC = () => {
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async (values: LoginForm) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/login', values);
      const { token, user, sessionToken, refreshToken, expiresAt } = response.data;
      
      login(user, token, sessionToken, refreshToken, expiresAt);
      message.success('Đăng nhập thành công!');
      navigate('/dashboard');
    } catch (error: any) {
      // Xử lý lỗi mà không reload page
      const errorMessage = error.response?.data?.message || error.message || 'Đăng nhập thất bại!';
      message.error(errorMessage);
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    // Xử lý lỗi validation mà không reload page
    console.log('Validation failed:', errorInfo);
    if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
      const firstError = errorInfo.errorFields[0];
      if (firstError.errors && firstError.errors.length > 0) {
        message.error(firstError.errors[0]);
      }
    }
  };

  // Xử lý đăng nhập Google
  const handleGoogleLogin = async (credentialResponse: any) => {
    try {
      setLoading(true);
      const { credential } = credentialResponse;
      if (!credential) {
        message.error('Không nhận được token từ Google!');
        return;
      }
      // Gửi token Google về backend
      const response = await api.post('/auth/google-login', { token: credential });
      
      if (response.data.status === 'pending') {
        message.info('Tài khoản Google của bạn đã được đăng ký thành công! Vui lòng chờ admin kích hoạt tài khoản.');
      } else if (response.data.token && response.data.user) {
        const { token, user, sessionToken, refreshToken, expiresAt } = response.data;
        login(user, token, sessionToken || '', refreshToken || '', expiresAt || '');
        message.success('Đăng nhập Google thành công!');
        navigate('/dashboard');
      } else {
        message.error(response.data.message || 'Đăng nhập Google thất bại!');
      }
    } catch (error: any) {
      console.error('Google login error:', error);
      message.error(error.response?.data?.message || 'Đăng nhập Google thất bại!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #ffffff, #faf5ff, #eff6ff)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        fontFamily: 'Inter, sans-serif'
      }}>
        <div className="login-container" style={{
          maxWidth: '480px',
          width: '100%',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}>
          {/* Login form */}
          <div className="login-form" style={{
            width: '100%',
            padding: '32px 28px',
            position: 'relative',
            zIndex: 10,
            background: 'rgba(255, 255, 255, 0.9)'
          }}>
            {/* Header */}
            <div style={{ 
              marginBottom: '24px', 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <a 
                href="https://thinksoft.com.vn/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                <img 
                  src="/logo/logo-header.png"
                  alt="Logo"
                  style={{
                    width: 'auto',
                    height: '60px',
                    maxWidth: '160px',
                    objectFit: 'contain'
                  }}
                />
              </a>
            </div>

            <h2 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              Chào mừng trở lại
            </h2>

            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: 'Vui lòng nhập tên đăng nhập!' }]}
                style={{ marginBottom: '16px' }}
              >
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#1f2937',
                    marginBottom: '6px',
                    textAlign: 'left'
                  }}>
                    Tên đăng nhập
                  </label>
                  <Input
                    prefix={
                      <UserOutlined 
                        style={{ 
                          color: '#666666',
                          fontSize: '14px'
                        }} 
                      />
                    }
                    placeholder="Nhập tên đăng nhập"
                    style={{
                      height: '38px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 2px rgba(37, 99, 235, 0.2)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: 'Vui lòng nhập mật khẩu!' }]}
                style={{ marginBottom: '16px' }}
              >
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#1f2937',
                    marginBottom: '6px',
                    textAlign: 'left'
                  }}>
                    Mật khẩu
                  </label>
                  <Input.Password
                    prefix={
                      <LockOutlined 
                        style={{ 
                          color: '#666666',
                          fontSize: '14px'
                        }} 
                      />
                    }
                    placeholder="Nhập mật khẩu"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    style={{
                      height: '38px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 2px rgba(37, 99, 235, 0.2)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </Form.Item>

              <Form.Item style={{ marginBottom: '20px' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{
                    height: '42px',
                    borderRadius: '6px',
                    background: '#2563eb',
                    border: 'none',
                    fontSize: '15px',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#1d4ed8';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#2563eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  {loading ? 'Đang đăng nhập...' : 'Đăng nhập'}
                </Button>
              </Form.Item>
            </Form>

            <div style={{
              marginTop: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <hr style={{ flex: 1, borderColor: '#d1d5db', borderWidth: '1px', margin: 0 }} />
              <span style={{
                margin: '0 12px',
                color: '#4b5563',
                fontSize: '13px',
                whiteSpace: 'nowrap'
              }}>
                Hoặc tiếp tục với
              </span>
              <hr style={{ flex: 1, borderColor: '#d1d5db', borderWidth: '1px', margin: 0 }} />
            </div>

            {/* Nút đăng nhập Google */}
            <div style={{ 
              marginTop: '16px', 
              textAlign: 'center'
            }}>
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => message.error('Đăng nhập Google thất bại!')}
                width="100%"
                useOneTap
              />
            </div>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginPage; 